import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { isDefined } from '@vueuse/core';
import { cloneDeep, isEqual, last } from 'lodash';

import { adaptRecipeIngredientsToItinerarySearchCriteria } from '@apps/main/modules/criteria/itinerarySearchCriteria/itinerariesCriteria';
import { hasChangedOnlySelectedDate } from '@apps/main/new-desktop/core/stores/criteria/criteriaStore.logic';
import type { StayIngredient } from '@apps/main/modules/recipe/ingredients/typeGuards';
import {
  isHotelIngredientAdapter,
  isOwnStayIngredient,
  isOwnStayIngredientAdapter,
  isStayIngredient,
} from '@apps/main/modules/recipe/ingredients/typeGuards';
import type { DestinationAvailabilityRecipe } from '@apps/main/modules/criteria/destinationAvailabilitySearchCriteria/destinationAvialabilityCriteria';
import { adaptRecipeToDestinationAvailabilitySearchCriteria } from '@apps/main/modules/criteria/destinationAvailabilitySearchCriteria/destinationAvialabilityCriteria';
import { getBaseConstraintQL } from '@apps/main/modules/criteria/ingredientAdapters/baseIngredientAdapter';
import { adaptHotelIngredientToHotelConstraintQL } from '@apps/main/modules/criteria/ingredientAdapters/activitiesInDestinationSearchAdapter/activitiesInDestinationSearchAdapter';

import {
  AutoCompleteSearchType,
  ProductSearchType,
  SelectedHotelTypeQL,
} from '@libs/trip-planner-interfaces';
import type {
  AccommodationAvailabilityUserCriteria,
  ActivitiesInDestinationCriteria,
  ActivityAvailabilityUserCriteria,
  ActivitySuggestionsSearchCriteria,
  BusinessCriteria,
  DestinationAvailabilitySearchUserCriteria,
  DestinationSuggestedActivitiesSearchCriteria,
  DestinationSuggestedActivitySearchCriteria,
  HotelConstraintSelectionQL,
  ItinerarySearchUserCriteria,
  ItinerarySearchUserCriteriaDependencies,
  SearchSuggestionCriteria,
  TransportAvailabilityUserCriteria,
} from '@libs/trip-planner-interfaces';
import { readonlyRef } from '@libs/utils';

import { useTripStore } from '../trip/tripStore';
import { useRecipeStore } from '../recipe/recipeStore';
import { useMetaStore } from '../meta/metaStore';
import type { DateWithItinerary } from '../trip/tripStore.model';
import { useProductDetailStore } from '../productDetail/productDetailStore';
import { useEditingIngredientStore } from '../editingIngredient/editingIngredientStore';
import { useItineraryStore } from '../itinerary/itineraryStore';
import {
  getEndSearchDateFromPreferredDay,
  getStartSearchDateFromPreferredDay,
} from '../preferredTimeStore/preferredTimeStoreLogic';
import { usePreferredTimeStore } from '../preferredTimeStore';

export interface DatesToSearch {
  startDate?: Date;
  endDate?: Date;
  selectedDate?: Date;
}

export const useCriteriaStore = defineStore('CriteriaStore', () => {
  const tripStore = useTripStore();
  const recipeStore = useRecipeStore();
  const preferredTimeStore = usePreferredTimeStore();
  const metaStore = useMetaStore();
  const itineraryStore = useItineraryStore();
  const editingIngredientStore = useEditingIngredientStore();
  const productDetailStore = useProductDetailStore();

  const itineraryCriteriaDependencies =
    computed<ItinerarySearchUserCriteriaDependencies>(() => ({
      ingredients: adaptRecipeIngredientsToItinerarySearchCriteria(
        recipeStore.recipe
      ),
      pax: recipeStore.pax.rooms,
      startSearchDate: preferredTimeStore.startSearchDate,
      endSearchDate: preferredTimeStore.endSearchDate,
      selectedDate: preferredTimeStore.preferredDay,
    }));

  const itineraryCriteria = ref<ItinerarySearchUserCriteria>({
    ...itineraryCriteriaDependencies.value,
    sessionId: tripStore.sessionId,
  });

  const businessCriteria = computed<BusinessCriteria>(() => ({
    affiliateId: metaStore.metaAffiliateId,
    metaData: recipeStore.metaData,
    searchForPackageRates: recipeStore.searchForPackageRates,
  }));

  const searchSuggestionCriteria = ref<SearchSuggestionCriteria>({
    searchText: '',
    searchType: AutoCompleteSearchType.Destination,
  });

  watch(
    itineraryCriteriaDependencies,
    (dependencies, oldDependencies) => {
      const isSearchedItinerary =
        itineraryStore.userSelectedItinerary?.isSearched;
      const isSearchingAvailability =
        itineraryStore.isSearchingItinerariesAvailability;
      const hasUserSelectedItinerary = isDefined(
        itineraryStore.userSelectedItinerary
      );

      if (
        (isSearchedItinerary ||
          (isSearchingAvailability && !hasUserSelectedItinerary)) &&
        hasChangedOnlySelectedDate(dependencies, oldDependencies)
      ) {
        return;
      }

      const selectedDate =
        isDefined(itineraryStore.userSelectedItinerary) &&
        !itineraryStore.userSelectedItinerary.isSearched
          ? dependencies.selectedDate
          : undefined;

      const finalStartDate =
        selectedDate ??
        (preferredTimeStore.preferredDay
          ? getStartSearchDateFromPreferredDay(preferredTimeStore.preferredDay)
          : dependencies.startSearchDate);
      const finalEndDate =
        selectedDate ??
        (preferredTimeStore.preferredDay
          ? getEndSearchDateFromPreferredDay(preferredTimeStore.preferredDay)
          : dependencies.endSearchDate);

      const newItineraryCriteria: ItinerarySearchUserCriteria = {
        ...dependencies,
        endSearchDate: finalEndDate,
        startSearchDate: finalStartDate,
        sessionId: tripStore.sessionId,
      };

      const isSameCriteria = isEqual(
        itineraryCriteria.value,
        newItineraryCriteria
      );

      if (isSameCriteria) {
        return;
      }

      itineraryCriteria.value = newItineraryCriteria;
    },
    { deep: true }
  );

  function getDatesToSearch(
    ingredient: StayIngredient,
    preferredDay?: Date
  ): DatesToSearch {
    if (!preferredDay) {
      return getDaysInMonthFromItineraryDates(itineraryStore.itineraryDates);
    }

    return getDaysWithSelectedDate(ingredient);
  }

  function getDaysWithSelectedDate(ingredient: StayIngredient): DatesToSearch {
    let ingredientDate = itineraryStore.getIngredientDate(
      ingredient.ingredientId
    );

    if (!ingredientDate && preferredTimeStore.preferredDay) {
      ingredientDate = recipeStore.getIngredientDateInRecipe(
        ingredient.ingredientId,
        preferredTimeStore.preferredDay
      );
    }

    if (!ingredientDate) {
      throw new Error('No date available on provided ingredient');
    }

    const monthDates = getDaysInMonthFromItineraryDates(
      itineraryStore.itineraryDates
    );

    return {
      startDate: monthDates.startDate,
      endDate: monthDates.endDate,
      selectedDate: ingredientDate,
    };
  }

  function getDaysInMonthFromItineraryDates(
    itineraryDates: DateWithItinerary[]
  ): DatesToSearch {
    const firstMonthDate = itineraryDates[0]?.date;
    const lastMonthDate = last(itineraryDates)?.date;

    if (!firstMonthDate || !lastMonthDate) {
      return {};
    }

    return {
      startDate: firstMonthDate,
      endDate: lastMonthDate,
    };
  }

  const activitySuggestionsCriteria = computed<
    ActivitySuggestionsSearchCriteria[]
  >(() => {
    const searchDate = itineraryStore.tripStartDate;

    if (!searchDate) {
      return [];
    }

    return recipeStore.stayOnlyRecipe.reduce(
      (acc: ActivitySuggestionsSearchCriteria[], ingredient, index: number) => {
        const constraint: HotelConstraintSelectionQL = getBaseConstraintQL(
          ingredient.searchTerm
        );

        const criteria = cloneDeep(itineraryCriteria.value);
        const ingredientFromCriteria = criteria.ingredients
          ? criteria.ingredients[index]
          : null;

        if (
          ingredientFromCriteria &&
          (isHotelIngredientAdapter(ingredientFromCriteria) ||
            isOwnStayIngredientAdapter(ingredientFromCriteria))
        ) {
          ingredientFromCriteria.constraint.selectedHotelTypes = [
            SelectedHotelTypeQL.Golf,
            SelectedHotelTypeQL.Ticket,
          ];
        }
        acc.push({
          userCriteria: {
            recipe: criteria,
            ingredientIndex: index,
            day: searchDate,
            constraint,
          },
          businessCriteria: businessCriteria.value,
        });

        return acc;
      },
      []
    );
  });

  const destinationAvailabilitySearchUserCriteria = computed<
    DestinationAvailabilitySearchUserCriteria | undefined
  >(() => {
    const destinationIngredient = editingIngredientStore.editingStayIngredient;

    if (!destinationIngredient || destinationIngredient.isNewIngredient) {
      return;
    }

    let datesToSearch: DatesToSearch = {};

    try {
      datesToSearch = getDatesToSearch(
        destinationIngredient,
        preferredTimeStore.preferredDay
      );
    } catch {
      return;
    }

    const destinationAvailabilityRecipe: DestinationAvailabilityRecipe = {
      destinationIngredient,
      itineraryCriteria: itineraryCriteria.value,
      searchActivities: recipeStore.searchActivities,
      datesToSearch,
    };

    return adaptRecipeToDestinationAvailabilitySearchCriteria(
      destinationAvailabilityRecipe
    );
  });

  const activityProductAvailabilityCriteria = computed<
    ActivityAvailabilityUserCriteria | undefined
  >(() => {
    const productCode = productDetailStore.destinationProductDetailCode;
    const ingredientId =
      productDetailStore.destinationProductDetailIngredientId;
    const isActivityProduct =
      productDetailStore.productSearchType === ProductSearchType.Activity;

    if (!productCode || !ingredientId || !isActivityProduct) {
      return;
    }

    const ingredientResponse =
      itineraryStore.getHotelIngredientResponse(ingredientId) ||
      itineraryStore.getOwnStayIngredientResponse(ingredientId);

    if (!ingredientResponse) {
      return;
    }

    const parentIngredientId = ingredientResponse.ingredientId;

    const ingredientIndex = recipeStore.recipe.findIndex(
      (ingredient) => ingredient.ingredientId === parentIngredientId
    );

    if (ingredientIndex === -1) {
      return;
    }

    const ingredient = recipeStore.getStayIngredient(parentIngredientId);

    const { startDate, endDate } = getDatesToSearch(
      ingredient,
      preferredTimeStore.preferredDay
    );

    const recipeWithDates: ItinerarySearchUserCriteria = {
      ...itineraryCriteria.value,
      startSearchDate: startDate,
      endSearchDate: endDate,
    };

    return {
      productCode,
      parentIngredientId,
      recipe: recipeWithDates,
      ingredientIndex,
    };
  });

  const destinationSuggestedActivitiesCriteria =
    computed<DestinationSuggestedActivitiesSearchCriteria>(() => {
      const NUMBER_OF_SUGGESTIONS = 3;
      const destinations: DestinationSuggestedActivitySearchCriteria[] =
        recipeStore.recipe.reduce(
          (
            suggestedActivitiesCriterias: DestinationSuggestedActivitySearchCriteria[],
            ingredient
          ) => {
            if (isStayIngredient(ingredient)) {
              suggestedActivitiesCriterias.push({
                constraint: adaptHotelIngredientToHotelConstraintQL(
                  ingredient,
                  NUMBER_OF_SUGGESTIONS
                ),
                ingredientId: ingredient.ingredientId,
              });
            }

            return suggestedActivitiesCriterias;
          },
          []
        );

      return { destinations };
    });

  const accommodationProductAvailabilityCriteria = computed<
    AccommodationAvailabilityUserCriteria | undefined
  >(() => {
    const productCode = productDetailStore.destinationProductDetailCode;
    const ingredientId =
      productDetailStore.destinationProductDetailIngredientId;
    const isHotelProduct =
      productDetailStore.productSearchType === ProductSearchType.Accommodation;

    if (!productCode || !ingredientId || !isHotelProduct) {
      return;
    }

    const ingredientIndex = recipeStore.recipe.findIndex(
      (ingredient) => ingredient.ingredientId === ingredientId
    );

    if (ingredientIndex === -1) {
      return;
    }

    const ingredient = recipeStore.getHotelIngredient(ingredientId);

    const { startDate, endDate, selectedDate } = getDatesToSearch(
      ingredient,
      preferredTimeStore.preferredDay
    );

    const recipeWithDates: ItinerarySearchUserCriteria = {
      ...itineraryCriteria.value,
      startSearchDate: startDate,
      endSearchDate: endDate,
    };

    return {
      recipe: recipeWithDates,
      ingredientIndex,
      productCode,
      day: selectedDate,
    };
  });

  const searchActivitiesInDestinationCriteria = computed<
    ActivitiesInDestinationCriteria[]
  >(() =>
    recipeStore.stayOnlyRecipe.map((stayIngredient) => ({
      ingredientId: stayIngredient.ingredientId,
      searchTerm: stayIngredient.searchTerm,
      nearbyPolygonText: isOwnStayIngredient(stayIngredient)
        ? undefined
        : stayIngredient.nearbyPolygon?.name,
      nearbyPolygonId: isOwnStayIngredient(stayIngredient)
        ? undefined
        : stayIngredient.nearbyPolygon?.id,
    }))
  );

  const transportAvailabilityCriteria = computed<
    TransportAvailabilityUserCriteria | undefined
  >(() => {
    if (
      !editingIngredientStore.editingTransportIngredient ||
      !editingIngredientStore.editingTransportIngredient.isIngredientCorrect()
    ) {
      return;
    }

    if (!itineraryStore.selectedItinerary?.date) {
      return;
    }

    return {
      day: itineraryStore.selectedItinerary.date,
      recipe: itineraryCriteria.value,
      ingredientIndex: recipeStore.recipe.indexOf(
        editingIngredientStore.editingTransportIngredient
      ),
      elementsInPage: 200,
      page: 0,
    };
  });

  return {
    itineraryCriteria: readonlyRef(itineraryCriteria),
    businessCriteria: readonlyRef(businessCriteria),
    searchSuggestionCriteria: readonlyRef(searchSuggestionCriteria),

    destinationAvailabilitySearchUserCriteria,
    accommodationProductAvailabilityCriteria,
    activityProductAvailabilityCriteria,
    searchActivitiesInDestinationCriteria,
    transportAvailabilityCriteria,
    activitySuggestionsCriteria,
    destinationSuggestedActivitiesCriteria,

    getDatesToSearch,
  };
});
